import { Button } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/ui/theme'
import ReverCard from '@/ui/components/rever-card'
import { ShieldCheckIcon } from '@/ui/icons/shield-check'

export interface ReceivedWithdrawal {
  id: string
  receivedAt: string
  orderShipped: boolean
  orderNumber: string
  email: string
}

export const WithdrawalReceived = ({
  withdrawal,
  onStartReturn
}: {
  withdrawal: ReceivedWithdrawal
  onStartReturn: () => void
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const receivedAt = new Date(withdrawal.receivedAt).toLocaleString(
    i18n.language,
    { dateStyle: 'long', timeStyle: 'long' }
  )

  return (
    <ReverCard className="lg:!p-8">
      <div className="flex flex-col items-center gap-4 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-light text-success-dark">
          <ShieldCheckIcon width={28} height={28} />
        </span>
        <h1 className="max-w-md text-2xl font-semibold leading-tight [text-wrap:balance]">
          {t('withdrawal.received_title')}
        </h1>
        <p className="text-sm leading-relaxed text-neutral-800 [text-wrap:pretty]">
          {t('withdrawal.received_description', {
            receivedAt,
            email: withdrawal.email,
            interpolation: { escapeValue: false }
          })}
        </p>
      </div>
      {withdrawal.orderShipped ? (
        <section className="mt-8 space-y-4 text-center">
          <h2 className="text-lg font-semibold leading-tight [text-wrap:balance]">
            {t('withdrawal.send_back_title')}
          </h2>
          <p className="text-sm leading-relaxed text-neutral-800 [text-wrap:pretty]">
            {t('withdrawal.send_back_description')}
          </p>
          <Button
            fullWidth
            size="lg"
            radius="sm"
            className="print:hidden"
            style={{
              backgroundColor: theme.colors.primary.main,
              color: theme.colors.primary.contrastText,
              borderRadius: theme.borderRadius
            }}
            onPress={onStartReturn}
          >
            {t('withdrawal.start_return')}
          </Button>
        </section>
      ) : null}
      <dl className="mt-8 space-y-3 rounded-lg border border-solid border-neutral-200 p-4 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-neutral-600">
            {t('withdrawal.order_number_label')}
          </dt>
          <dd className="break-all text-right">{withdrawal.orderNumber}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-neutral-600">
            {t('withdrawal.reference_label')}
          </dt>
          <dd className="break-all text-right font-mono text-xs leading-5">
            {withdrawal.id}
          </dd>
        </div>
      </dl>
      <Button
        fullWidth
        size="lg"
        radius="sm"
        variant="bordered"
        className="mt-4 print:hidden"
        style={{ borderRadius: theme.borderRadius }}
        onPress={() => window.print()}
      >
        {t('withdrawal.print')}
      </Button>
    </ReverCard>
  )
}
